import * as React from 'react'
import { View, Text } from 'react-native'
import styled from '@theme/styled-components'

import TextInput from '@components/textInput/TextInput'
import Tag from '@components/tag/Tag'
import Switch from '@components/switch/Switch'

import MetierFilter from '@components/filter/ActivityFilter'
import SpaceFilter from '@components/filter/SpaceFilter'
import ManifestationFilter from '@components/filter/ManifestationFilter'
import SocieteIncidentFilter from '@components/filter/SocieteIncidentFilter'
import UserFilter from '@components/filter/UserFilter'
import StatusFilter from './StatusFilter'
import CategoryFilter from './CategoryFilter'
import CalendarDatePicker from '@components/picker/CalendarDatePicker'

import FilterLayout from '@components/filter/FilterLayout'
import GroupedFilterLayout from './GroupedFilterLayout'

import useReducer from '@store/useReducer'
import * as FiltreStore from '@store/filtre/filtre'
import * as FamilleStore from '@store/famille/famille'
import * as SiteStore from '@store/site/site'

import useDebounce from '@utils/useDebounce'
import useI18n from '@i18n/useI18n'
import useBreakpoint from '@utils/useBreakpoints'
import useFilterDisplay, { Filter } from './useFilterDisplay'

interface Props {
  display?: Filter[]
  places: LocalisationResponse[]
}

const IncidentFilters = ({ display, places }: Props) => {
  const i18n = useI18n()
  const [, bp] = useBreakpoint()

  const displayFilters = useFilterDisplay(display)

  const filtres = useReducer(FiltreStore.store, (s) => s.incident)
  const activities = useReducer(FamilleStore.store, (s) => s.activities)
  const manifestations = useReducer(SiteStore.store, (s) => s.manifestations)
  const societes = useReducer(FiltreStore.store, (s) => s.societesIncident)
  const users = useReducer(FiltreStore.store, (s) => s.users)

  const [open, setOpen] = React.useState<Filter>()
  const [search, bounced, setSearch] = useDebounce(filtres.search)

  React.useEffect(() => {
    FiltreStore.actions.setIncidentFilter({ search: bounced })
  }, [bounced])

  if (bp === 'phone') {
    return <GroupedFilterLayout filtres={filtres} displayFilters={displayFilters} places={places} />
  }

  return (
    <MainContainer>
      <TextInput
        onChange={setSearch}
        placeholder={i18n.t('screens.incidentList.search')}
        left={{ name: 'search' }}
        value={search}
      />
      <Container>
        <Line>
          {displayFilters.includes('manifestation') && (
            <FilterContainer idx={0}>
              <FilterLayout
                open={open === 'manifestation'}
                onOpen={(value) => setOpen(value ? 'manifestation' : undefined)}
                name={`${i18n.t('screens.incidentList.filters.tags.manifestation')}${
                  filtres.manifestation.length > 0 ? `: ${filtres.manifestation.length}` : ''
                }`}
                active={filtres.manifestation.length > 0}>
                <ManifestationFilter
                  manifestations={manifestations}
                  onManifestationsSelected={(values) => {
                    FiltreStore.actions.setIncidentFilter({ manifestation: values })
                    setOpen(undefined)
                  }}
                  selected={filtres.manifestation}
                />
              </FilterLayout>
            </FilterContainer>
          )}

          {displayFilters.includes('activity') && (
            <FilterContainer idx={1}>
              <FilterLayout
                open={open === 'activity'}
                onOpen={(value) => setOpen(value ? 'activity' : undefined)}
                name={`${i18n.t('screens.incidentList.filters.tags.activity')}${
                  filtres.activity.length > 0 ? `: ${filtres.activity.length}` : ''
                }`}
                active={filtres.activity.length > 0}>
                <MetierFilter
                  activities={activities}
                  onActivitiesSelected={(values) => {
                    FiltreStore.actions.setIncidentFilter({ activity: values })
                    setOpen(undefined)
                  }}
                  selected={filtres.activity}
                />
              </FilterLayout>
            </FilterContainer>
          )}

          {displayFilters.includes('societe') && (
            <FilterContainer idx={2}>
              <FilterLayout
                open={open === 'societe'}
                onOpen={(value) => setOpen(value ? 'societe' : undefined)}
                name={`${i18n.t('screens.incidentList.filters.tags.societe')}${
                  filtres.societe.length > 0 ? `: ${filtres.societe.length}` : ''
                }`}
                active={filtres.societe.length > 0}>
                <SocieteIncidentFilter
                  societes={societes}
                  onSocietesSelected={(values) => {
                    FiltreStore.actions.setIncidentFilter({ societe: values })
                    setOpen(undefined)
                  }}
                  selected={filtres.societe}
                />
              </FilterLayout>
            </FilterContainer>
          )}

          {displayFilters.includes('status') && (
            <FilterContainer idx={3}>
              <FilterLayout
                open={open === 'status'}
                onOpen={(value) => setOpen(value ? 'status' : undefined)}
                name={`${i18n.t('screens.incidentList.filters.tags.status')}${
                  filtres.status.length > 0 ? `: ${filtres.status.length}` : ''
                }`}
                active={filtres.status.length > 0}>
                <StatusFilter
                  onStatusSelected={(values) => {
                    FiltreStore.actions.setIncidentFilter({ status: values })
                    setOpen(undefined)
                  }}
                  selected={filtres.status}
                />
              </FilterLayout>
            </FilterContainer>
          )}

          {displayFilters.includes('category') && (
            <FilterContainer idx={4}>
              <FilterLayout
                open={open === 'category'}
                onOpen={(value) => setOpen(value ? 'category' : undefined)}
                name={`${i18n.t('screens.incidentList.filters.tags.category')}${
                  filtres.category.length > 0 ? `: ${filtres.category.length}` : ''
                }`}
                active={filtres.category.length > 0}>
                <CategoryFilter
                  onCategoriesSelected={(values) => {
                    FiltreStore.actions.setIncidentFilter({ category: values })
                    setOpen(undefined)
                  }}
                  selected={filtres.category}
                />
              </FilterLayout>
            </FilterContainer>
          )}

          {displayFilters.includes('location') && (
            <FilterContainer idx={5}>
              <FilterLayout
                open={open === 'location'}
                onOpen={(value) => setOpen(value ? 'location' : undefined)}
                name={`${i18n.t('screens.incidentList.filters.tags.location')}${
                  filtres.location.length > 0 ? `: ${filtres.location.length}` : ''
                }`}
                active={filtres.location.length > 0}>
                <SpaceFilter
                  places={places}
                  onPlacesSelected={(values) => {
                    FiltreStore.actions.setIncidentFilter({ location: values })
                    setOpen(undefined)
                  }}
                  selected={filtres.location}
                />
              </FilterLayout>
            </FilterContainer>
          )}

          {displayFilters.includes('user') && (
            <FilterContainer idx={6}>
              <FilterLayout
                open={open === 'user'}
                onOpen={(value) => setOpen(value ? 'user' : undefined)}
                name={`${i18n.t('screens.incidentList.filters.tags.user')}${
                  filtres.user.length > 0 ? `: ${filtres.user.length}` : ''
                }`}
                active={filtres.user.length > 0}>
                <UserFilter
                  users={users}
                  onUsersSelected={(values) => {
                    FiltreStore.actions.setIncidentFilter({ user: values })
                    setOpen(undefined)
                  }}
                  selected={filtres.user}
                />
              </FilterLayout>
            </FilterContainer>
          )}

          {displayFilters.includes('from') && displayFilters.includes('to') && (
            <FilterContainer idx={7}>
              <Tag
                item={{
                  label:
                    filtres.from && filtres.to
                      ? i18n.t('filters.tags.range', { from: new Date(filtres.from), to: new Date(filtres.to) })
                      : filtres.from
                      ? i18n.t('filters.tags.from', { from: new Date(filtres.from) })
                      : filtres.to
                      ? i18n.t('filters.tags.to', { to: new Date(filtres.to) })
                      : i18n.t('filters.tags.date'),
                  value: !!filtres.to,
                }}
                onItemSelected={() =>
                  CalendarDatePicker.open({
                    mode: 'popup',
                    onDates: (from, to) =>
                      FiltreStore.actions.setIncidentFilter({
                        from: from ? from.toISOString() : undefined,
                        to: to ? to.toISOString() : undefined,
                      }),
                    start: filtres.from ? new Date(filtres.from) : undefined,
                    end: filtres.to ? new Date(filtres.to) : undefined,
                    max: new Date(),
                    required: false,
                  })
                }
                selected={!!filtres.to || !!filtres.from}
                inactiveColor
              />
            </FilterContainer>
          )}

          {displayFilters.includes('created') && (
            <FilterContainer idx={8}>
              <Tag
                item={{ label: i18n.t('screens.incidentList.filters.tags.created'), value: filtres.created }}
                onItemSelected={() => FiltreStore.actions.setIncidentFilter({ created: !filtres.created })}
                selected={filtres.created}
                inactiveColor
              />
            </FilterContainer>
          )}

          {displayFilters.includes('followed') && (
            <FilterContainer idx={9}>
              <Tag
                item={{ label: i18n.t('screens.incidentList.filters.tags.followed'), value: filtres.followed }}
                onItemSelected={() => FiltreStore.actions.setIncidentFilter({ followed: !filtres.followed })}
                selected={filtres.followed}
                inactiveColor
              />
            </FilterContainer>
          )}

          {displayFilters.includes('new') && (
            <FilterContainer idx={10}>
              <Tag
                item={{ label: i18n.t('screens.incidentList.filters.tags.isNew'), value: filtres.isNew }}
                onItemSelected={() => FiltreStore.actions.setIncidentFilter({ isNew: !filtres.isNew })}
                selected={filtres.isNew}
                inactiveColor
              />
            </FilterContainer>
          )}

          {displayFilters.includes('prioritaire') && (
            <FilterContainer idx={11}>
              <Tag
                item={{ label: i18n.t('screens.incidentList.filters.tags.prioritaire'), value: filtres.prioritaire }}
                onItemSelected={() => FiltreStore.actions.setIncidentFilter({ prioritaire: !filtres.prioritaire })}
                selected={filtres.prioritaire}
                inactiveColor
              />
            </FilterContainer>
          )}

          <ResetText onPress={() => FiltreStore.actions.resetIncidentFilter()}>
            {i18n.t('screens.incidentList.filters.reset')}
          </ResetText>
        </Line>
        <SwitchView>
          <SwitchText>{i18n.t('screens.incidentList.filters.switch')}</SwitchText>
          <Switch
            onChange={() => FiltreStore.actions.setIncidentFilter({ isMap: !filtres.isMap })}
            value={filtres.isMap}
          />
        </SwitchView>
      </Container>
    </MainContainer>
  )
}

export default IncidentFilters

const MainContainer = styled(View)`
  margin-top: 32px;
  flex-direction: column-reverse;
`

const Container = styled(View)`
  flex-direction: row;
  justify-content: space-between;
`

const Line = styled(View)`
  flex-flow: row wrap;
  align-items: center;
  flex: 1;
  margin-right: 40px;
`

const ResetText = styled(Text)`
  ${(props) => props.theme.fonts.Body};
  font-size: 16px;
  text-decoration: underline;
  margin: 16px 0;
  z-index: -10;
`

const SwitchView = styled(View)`
  flex-direction: row;
  height: 54px;
  align-items: center;
`

const SwitchText = styled(Text)`
  ${(props) => props.theme.fonts.Body};
  font-size: 16px;
  margin-right: 10px;
`

const FilterContainer = styled(View)<{ idx: number }>`
  margin: 8px 0;
  margin-right: 8px;
  z-index: -${(props) => props.idx};
`
